import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { StorageService } from './storage.service';

export interface RegistrationData {
    tempRegistrationCode: string;
    postcode?: string;
    organisation_id?: number;
    organisation_name?: string;
    suborg_id?: number;
    suborg_name?: string;
    local_service_option_id?: number;
    selectedOrganisation?: any;
    organisations?: any[];
    ageAgreementCompleted?: boolean;
    ageAgreementReturnUrl?: string;
    registrationMethod?: 'nhs' | 'manual';
    nhsUserData?: any;
    createdAt?: Date;
    updatedAt?: Date;
    gp_id?: number; // For NHS registration
    gp_name?: string; // For NHS registration
    dob_uk?: string; // For NHS registration
    dob?: string; // For NHS registration
    email?: string; // For manual registration
    name?: string; // For manual registration
    [key: string]: any; // Allow additional properties
}

@Injectable({
    providedIn: 'root'
})
export class RegistrationService {

    constructor(
        private router: Router,
        private storage: StorageService
    ) { }

    /**
     * Check if we need to show the age agreement page
     * @param organisation - The organisation object with minimumAge property
     * @returns boolean - Whether age agreement is needed
     */
    async needsAgeAgreement(organisation?: any): Promise<boolean> {
        const registrationData = await this.getRegistrationData();
        return !registrationData?.ageAgreementCompleted;
    }

    /**
     * Get the minimum age for the organisation
     * @param organisation - The organisation object
     * @returns number - The minimum age (default 18 if null/undefined)
     */
    getMinimumAge(organisation: any): number {
        return organisation?.minimumAge || organisation?.minimum_age || 16;
    }

    async storeMinimumAgeValue(age: number): Promise<void> {
        this.storage.setData('minimumAge', age);
    }

    /**
     * Navigate to age agreement page
     * @param organisation - The organisation object
     * @param returnUrl - The URL to return to after age agreement
     */
    async navigateToAgeAgreement(organisation: any, returnUrl: string): Promise<void> {
        // Store the return URL and organisation info in the current registration
        await this.updateRegistrationData({
            ageAgreementReturnUrl: returnUrl,
            selectedOrganisation: organisation
        });

        this.router.navigate(['/agreement/age-agreement']);
    }


    /**
     * Get the register object from Dexie storage (legacy compatibility)
     */
    async getRegisterObject(): Promise<any> {
        let registrationData = await this.getRegistrationData();
        if (!registrationData) return {};

        let localStorageData: any = {};
        localStorageData = JSON.parse(localStorage.getItem('registerobject') || '{}');
        // Get localStorage data (contains personal details: name, phone, etc.)
        let returnData: any = {};

        // Merge both sources: localStorage (personal) + RegistrationService (suborg)
        returnData = { ...registrationData, ...localStorageData };

        console.log('Merged registration data:', returnData);
        return returnData;
    }

    /**
     * Check if the current registration flow should show age agreement
     * This is called from postcode-entry and select-super-organisation pages
     */
    async shouldShowAgeAgreement(organisations: any[], selectedOrgId?: number): Promise<{ show: boolean, organisation?: any, returnUrl?: string }> {
        const registrationData = await this.getRegistrationData();

        // Always show age agreement if we have an organisation selected
        // Remove the check for already completed since we want to ensure proper flow

        let targetOrganisation: any = null;
        let returnUrl: string = '';

        if (selectedOrgId) {
            // Coming from select-super-organisation
            targetOrganisation = organisations.find(org => org.id == selectedOrgId);
            returnUrl = '/select-super-organisation-continue';
        } else if (organisations.length === 1) {
            // Coming from postcode-entry with single organisation
            targetOrganisation = organisations[0];
            returnUrl = '/create-account';
        }

        if (targetOrganisation) {
            return {
                show: true,
                organisation: targetOrganisation,
                returnUrl: returnUrl
            };
        }

        return { show: false };
    }

    /**
     * Mark age agreement as completed
     */
    async completeAgeAgreement(): Promise<void> {
        await this.updateRegistrationData({ ageAgreementCompleted: true });
    }

    /**
     * Store organisation data for use after age agreement
     */
    async storeOrganisationData(organisation: any): Promise<void> {
        await this.updateRegistrationData({
            selectedOrganisation: organisation,
            organisation_id: organisation.id,
            organisation_name: organisation.name
        });

        // Also store in localStorage for backward compatibility
        localStorage.setItem('localServices', JSON.stringify(organisation.local_service_options));
    }

    /**
     * Get stored organisation data
     */
    async getStoredOrganisation(): Promise<any> {
        const registrationData = await this.getRegistrationData();

        // First try to get from registrationData
        if (registrationData?.selectedOrganisation) {
            return registrationData.selectedOrganisation;
        }

        // Try to find from organisations in localStorage for backward compatibility
        if (registrationData?.organisation_id) {
            try {
                const organisations = JSON.parse(localStorage.getItem('organisations') || '[]');
                const found = organisations.find(org => org.id == registrationData.organisation_id);
                if (found) {
                    return found;
                }
            } catch (err) {
                console.warn('Could not parse organisations from localStorage:', err);
            }
        }

        return null;
    }

    /**
     * Continue registration flow after age agreement based on stored organisation data
     */
    async continueRegistrationFlow(dataService: any): Promise<void> {
        const registrationData = await this.getRegistrationData();
        const organisation = await this.getStoredOrganisation();

        if (!organisation) {
            console.warn('No organisation found, going to create-account');
            this.router.navigate(['/create-account']);
            return;
        }

        // Clear temporary data
        await this.updateRegistrationData({ ageAgreementReturnUrl: undefined });

        // Proceed based on organisation structure
        if (organisation.local_service_options?.length === 1) {
            // Single local service option - search for practices
            await this.handleSingleLocalServiceOption(organisation, registrationData, dataService);
        } else if (organisation.local_service_options?.length > 1) {
            // Multiple local service options
            this.router.navigate(['/local-service-options']);
        } else {
            // No local service options
            this.router.navigate(['/create-account']);
        }
    }

    /**
     * Handle single local service option flow
     */
    private async handleSingleLocalServiceOption(organisation: any, registrationData: any, dataService: any): Promise<void> {
        const localServiceOption = organisation.local_service_options[0];

        await this.updateRegistrationData({
            local_service_option_id: localServiceOption.id,
            organisation_id: organisation.id
        });

        dataService.showAjaxRequestLoader = true;
        const payload = {
            search: '',
            postcode: registrationData?.postcode?.trim() || '',
            local_service_option_id: localServiceOption.id,
            organisation_id: organisation.id
        };

        dataService.http.post(dataService.basePath + 'api/searchsuborg', dataService.payloadMiddleware(payload)).subscribe({
            next: async (result: any) => {
                dataService.showAjaxRequestLoader = false;

                if (result.items?.length === 0) {
                    // No practices found
                    console.warn('No practices found for organisation');
                    this.router.navigate(['/create-account']);
                } else if (localServiceOption.page_template === 'radio' &&
                    result.items.length === 1 &&
                    localServiceOption.automatically_assign === 1) {
                    // Single practice, auto-assign
                    await this.updateRegistrationData({
                        suborg_id: result.items[0].id,
                        suborg_name: result.items[0].name,
                        organisation_name: result.items[0].organisation?.name || organisation.name
                    });

                    if (dataService.isUserLogged) {
                        dataService.setUserSettings([{ postcode: registrationData?.postcode }]);
                        dataService.apiPostPremise('nhs/update-suborg', dataService.payloadMiddleware({
                            suborg_id: result.items[0].id
                        })).then(() => {
                            this.router.navigate(['/accesscodes']);
                        });
                    } else {
                        this.router.navigate(['/create-account']);
                    }
                } else {
                    // Multiple practices - let user select
                    localStorage.setItem('suborgs', JSON.stringify(result.items));
                    this.router.navigate(['/select-practice/' + localServiceOption.page_url]);
                }
            },
            error: (error: any) => {
                dataService.showAjaxRequestLoader = false;
                console.error('Error searching practices:', error);
                this.router.navigate(['/create-account']);
            }
        });
    }

    /**
     * Store selected practice (suborg/gp) in registration data
     */
    async setSelectedPractice(suborg: any): Promise<void> {
        await this.updateRegistrationData({
            suborg_id: suborg.id,
            suborg_name: suborg.name,
            organisation_id: suborg.organisation?.id || suborg.organisation_id,
            organisation_name: suborg.organisation?.name || suborg.organisation_name
        });

        // Sync with localStorage for backward compatibility
        await this.syncWithLocalStorage();
    }

    /**
     * Generate a unique temporary registration code
     */
    private generateTempCode(): string {
        // 5 alphanumeric characters
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let tempCode = '';
        for (let i = 0; i < 5; i++) {
            tempCode += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return tempCode;
    }

    /**
     * Initialize registration flow with a new temp registration code
     * Should be called at the start of registration (postcode entry)
     */
    async initializeRegistration(postcode?: string): Promise<string> {
        const tempCode = this.generateTempCode();

        const registrationData: RegistrationData = {
            tempRegistrationCode: tempCode,
            postcode: postcode?.trim(),
            createdAt: new Date(),
            updatedAt: new Date()
        };

        // Store in Dexie for persistence
        await this.storage.setDexie('registrationSteps', tempCode, registrationData);

        // Also store current temp code reference
        await this.storage.setDexie('registrationSteps', 'current_temp_code', tempCode);

        return tempCode;
    }

    /**
     * Get current temp registration code
     */
    async getCurrentTempCode(): Promise<string | null> {
        return await this.storage.getDexie('registrationSteps', 'current_temp_code', null);
    }

    /**
     * Get registration data by temp code
     */
    async getRegistrationData(tempCode?: string): Promise<RegistrationData | null> {
        if (!tempCode) {
            tempCode = await this.getCurrentTempCode();
        }

        if (!tempCode) return null;

        return await this.storage.getDexie('registrationSteps', tempCode, null);
    }

    /**
     * Update registration data for current temp code
     */
    async updateRegistrationData(updates: Partial<RegistrationData>): Promise<void> {
        const tempCode = await this.getCurrentTempCode();
        if (!tempCode) {
            throw new Error('No active registration found. Please start registration from postcode entry.');
        }

        const existingData = await this.getRegistrationData(tempCode);
        if (!existingData) {
            throw new Error('Registration data not found for temp code: ' + tempCode);
        }

        const updatedData: RegistrationData = {
            ...existingData,
            ...updates,
            updatedAt: new Date()
        };

        await this.storage.setDexie('registrationSteps', tempCode, updatedData);
    }

    /**
     * Clear registration data (after successful completion or abandonment)
     */
    async clearRegistrationData(tempCode?: string): Promise<void> {
        if (!tempCode) {
            tempCode = await this.getCurrentTempCode();
        }

        if (tempCode) {
            await this.storage.removeDexie('registrationSteps', tempCode);
        }

        // Clear current temp code reference
        await this.storage.removeDexie('registrationSteps', 'current_temp_code');
    }

    /**
     * Get backend payload with all registration data
     */
    async getBackendPayload(): Promise<any> {
        const registrationData = await this.getRegistrationData();
        if (!registrationData) return {};

        const payload: any = {};

        // Add temp registration code
        if (registrationData.tempRegistrationCode) {
            payload.temp_registration_code = registrationData.tempRegistrationCode;
        }

        // Add location and organisation data
        if (registrationData.postcode) payload.postcode = registrationData.postcode;
        if (registrationData.organisation_id) payload.organisation_id = registrationData.organisation_id;
        if (registrationData.organisation_name) payload.organisation_name = registrationData.organisation_name;
        if (registrationData.suborg_id) payload.suborg_id = registrationData.suborg_id;
        if (registrationData.suborg_name) payload.suborg_name = registrationData.suborg_name;
        if (registrationData.local_service_option_id) payload.local_service_option_id = registrationData.local_service_option_id;
        if (registrationData.registrationMethod) payload.registration_method = registrationData.registrationMethod;

        // Include NHS data for NHS registration
        if (registrationData.registrationMethod === 'nhs' && registrationData.nhsUserData) {
            payload.nhs_user_data = registrationData.nhsUserData;
        }

        return payload;
    }

    /**
     * Set NHS user data for current registration
     */
    async setNhsUserData(nhsData: any): Promise<void> {
        await this.updateRegistrationData({
            nhsUserData: nhsData,
            registrationMethod: 'nhs'
        });
    }

    /**
     * Store postcode and organisation data
     */
    async setLocationData(postcode: string, organisations: any[]): Promise<void> {
        const updateData: Partial<RegistrationData> = {
            postcode: postcode.trim(),
            organisations: organisations
        };

        // If single organisation, store it automatically
        if (organisations.length === 1) {
            const org = organisations[0];
            updateData.organisation_id = org.id;
            updateData.organisation_name = org.name;
            updateData.selectedOrganisation = org;
        }

        await this.updateRegistrationData(updateData);
    }

    /**
     * Sync with legacy localStorage for backward compatibility
     */
    async syncWithLocalStorage(): Promise<void> {
        const registrationData = await this.getRegistrationData();
        if (registrationData) {
            const legacyFormat = {
                tempRegistrationCode: registrationData.tempRegistrationCode,
                postcode: registrationData.postcode,
                organisation_id: registrationData.organisation_id,
                organisation_name: registrationData.organisation_name,
                suborg_id: registrationData.suborg_id,
                suborg_name: registrationData.suborg_name,
                local_service_option_id: registrationData.local_service_option_id,
                selectedOrganisation: registrationData.selectedOrganisation,
                ageAgreementCompleted: registrationData.ageAgreementCompleted
            };

            localStorage.setItem('registerobject', JSON.stringify(legacyFormat));
        }
    }

    /**
     * Collection-like update method similar to Eloquent
     * Usage: await registrationService.where('tempRegistrationCode', 'nxs22').update({ organisation: newOrg })
     */
    where(field: keyof RegistrationData, value: any) {
        return {
            update: async (updates: Partial<RegistrationData>): Promise<void> => {
                if (field === 'tempRegistrationCode') {
                    // Direct update by temp code
                    const existingData = await this.storage.getDexie('registrationSteps', value, null);
                    if (existingData) {
                        const updatedData: RegistrationData = {
                            ...existingData,
                            ...updates,
                            updatedAt: new Date()
                        };
                        await this.storage.setDexie('registrationSteps', value, updatedData);
                    }
                } else {
                    // For other fields, get current registration and update if it matches
                    const tempCode = await this.getCurrentTempCode();
                    if (tempCode) {
                        const existingData = await this.getRegistrationData(tempCode);
                        if (existingData && existingData[field] === value) {
                            await this.updateRegistrationData(updates);
                        }
                    }
                }
            }
        };
    }

    /**
     * Get minimum age from stored organisation data
     */
    async getMinimumAgeFromRegistration(): Promise<number> {
        const registrationData = await this.getRegistrationData();
        if (registrationData?.selectedOrganisation) {
            return this.getMinimumAge(registrationData.selectedOrganisation);
        }
        return 18;
    }

    /**
     * Get organisation data from current registration
     */
    async getOrganisationFromRegistration(): Promise<any> {
        const registrationData = await this.getRegistrationData();
        return registrationData?.selectedOrganisation || null;
    }

    /**
     * Set registration method (NHS or manual)
     */
    async setRegistrationMethod(method: 'nhs' | 'manual'): Promise<void> {
        await this.updateRegistrationData({ registrationMethod: method });
    }

    /**
     * Check and capture any suborg data from organisation responses that might include direct suborg assignment
     */
    async checkAndCaptureSuborgFromOrganisation(organisation: any): Promise<void> {
        // Some APIs might return direct suborg assignment in organisation structure
        if (organisation.default_suborg_id || organisation.suborg_id) {
            const suborgId = organisation.default_suborg_id || organisation.suborg_id;
            await this.updateRegistrationData({
                suborg_id: suborgId,
                suborg_name: organisation.default_suborg_name || organisation.suborg_name
            });
            console.log('Captured direct suborg_id from organisation:', suborgId);
        }

        // Check if organisation has a single local service option with auto-assignment
        if (organisation.local_service_options?.length === 1) {
            const localService = organisation.local_service_options[0];
            if (localService.suborg_id && localService.automatically_assign === 1) {
                await this.updateRegistrationData({
                    suborg_id: localService.auto_assign_suborg_id || localService.suborg_id,
                    suborg_name: localService.auto_assign_suborg_name || localService.suborg_name
                });
                console.log('Captured auto-assigned suborg_id from local service:', localService.auto_assign_suborg_id);
            }
        }
    }
}
